import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { isNative } from '@/shared/api/platform'
import { ToggleRow } from '@/shared/components/ToggleRow'
import { isPrerendering } from '@/shared/lib/prerender'

import {
  type ConsentPreferences,
  useConsentStore,
  useShouldShowBanner,
} from '../store/consentStore'

const BUTTON_BASE =
  'brutal-sm brutal-press flex-1 rounded-md px-4 py-2 font-brutal text-[11px] font-bold uppercase tracking-widest text-foreground sm:flex-none'

/**
 * Banner sticky-bottom de consent de cookies (RGPD/LSSI).
 *
 * <p>Aparece cuando {@link useShouldShowBanner} devuelve true (sin decisión
 * guardada, política con versión mayor, o user pulsó "Gestionar cookies"
 * desde el footer).</p>
 *
 * <p>Primera capa: "Solo necesarias", "Configurar" y "Aceptar todo" al mismo
 * nivel jerárquico — RGPD pide que rechazar no sea más difícil ni menos
 * visible que aceptar. "Configurar" abre la segunda capa con un interruptor
 * por finalidad (guía de cookies de la AEPD). El link a la política vive
 * dentro del banner para que el user pueda informarse antes de decidir.</p>
 *
 * <p>Categorías: <strong>necesarias</strong> (refresh_token cookie — siempre
 * activa, no se pregunta), <strong>analíticas</strong> (New Relic Browser +
 * Microsoft Clarity) y <strong>publicidad</strong> (píxel de Meta). Las dos
 * últimas, opt-in.</p>
 */
export function CookieBanner() {
  const visible = useShouldShowBanner()

  // En la app nativa NO se muestra: allí no corre ninguna analítica ni el
  // píxel (New Relic sin envs; Clarity/Umami/Meta solo en el dominio prod —
  // no en capacitor://localhost) y la auth va por Keychain, no por cookie → no
  // hay nada que consentir. Mostrarlo hacía que la revisión de Apple pidiera
  // ATT (Guideline 5.1.2(i)). El "Gestionar cookies" del footer se oculta igual.
  // En el prerender de build tampoco: el aviso no es contenido de la página (lo pintaría en
  // el HTML estático que leen buscadores y agentes).
  if (!visible || isNative() || isPrerendering()) return null

  // Panel aparte: se monta de cero en cada apertura, así la capa "Configurar"
  // arranca cerrada y con la decisión vigente (p. ej. al reabrir desde el footer).
  return <CookieBannerPanel />
}

function CookieBannerPanel() {
  const { t } = useTranslation()
  const acceptAll = useConsentStore((s) => s.acceptAll)
  const rejectAll = useConsentStore((s) => s.rejectAll)
  const savePreferences = useConsentStore((s) => s.savePreferences)
  const [configuring, setConfiguring] = useState(false)
  const [prefs, setPrefs] = useState<ConsentPreferences>(() => {
    const decision = useConsentStore.getState().decision
    return { analytics: decision?.analytics ?? false, marketing: decision?.marketing ?? false }
  })

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t('consent.banner.ariaLabel')}
      className="fixed inset-x-0 bottom-0 z-40 px-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:px-4 sm:pb-[calc(1rem+env(safe-area-inset-bottom))]"
    >
      <div className="brutal-lg mx-auto max-w-3xl rounded-2xl bg-background p-4 sm:p-5">
        {/* `<a>` con href en vez de `<Link>` de react-router porque
            CookieBanner vive fuera del <RouterProvider> (montado en App.tsx
            a nivel raíz para aparecer también durante el AuthBootSplash).
            createBrowserRouter NO expone su context al exterior. Full
            reload al click — aceptable para una página legal. */}
        <p className="mb-3 font-display text-sm leading-snug sm:text-base">
          {t('consent.banner.body')}{' '}
          <a href="/privacy#cookies" className="font-medium text-blue underline">
            {t('consent.banner.policyLink')}
          </a>
        </p>
        {configuring && (
          <div className="mb-3 space-y-2">
            <ToggleRow
              title={t('consent.banner.analyticsTitle')}
              description={t('consent.banner.analyticsDesc')}
              checked={prefs.analytics}
              onToggle={() => setPrefs((p) => ({ ...p, analytics: !p.analytics }))}
            />
            <ToggleRow
              title={t('consent.banner.marketingTitle')}
              description={t('consent.banner.marketingDesc')}
              checked={prefs.marketing}
              onToggle={() => setPrefs((p) => ({ ...p, marketing: !p.marketing }))}
            />
          </div>
        )}
        <div className="flex flex-wrap gap-2 sm:justify-end">
          <button type="button" onClick={rejectAll} className={`${BUTTON_BASE} bg-background-alt`}>
            {t('consent.banner.reject')}
          </button>
          {configuring ? (
            <button
              type="button"
              onClick={() => savePreferences(prefs)}
              className={`${BUTTON_BASE} bg-background-alt`}
            >
              {t('consent.banner.save')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setConfiguring(true)}
              className={`${BUTTON_BASE} bg-background-alt`}
            >
              {t('consent.banner.configure')}
            </button>
          )}
          <button type="button" onClick={acceptAll} className={`${BUTTON_BASE} bg-yellow`}>
            {t('consent.banner.accept')}
          </button>
        </div>
      </div>
    </div>
  )
}
